import { Line as ZeroLine } from '@visx/shape';
import { type MutableRefObject } from 'react';

import { ContextValueIndicator } from './components/ContextValueIndicator.js';
import {
  AXIS_HEIGHT,
  CONTEXT_VALUE_MIN_LABELS_DISTANCE_LARGE,
  CONTEXT_VALUE_MIN_LABELS_DISTANCE_SMALL,
  CONTEXT_VALUE_TEXT_OFFSET_MAX,
  CONTEXT_VALUE_TEXT_OFFSET_MIN_LARGE,
  CONTEXT_VALUE_TEXT_OFFSET_MIN_SMALL,
  DEFAULT_SCALE_FALLBACK_COLOR,
  DEFAULT_SHOW_CONTEXT_VALUES,
  defaultYAxisConfig,
  FONT_SIZE_BREAKING_POINT,
  SPARKLINE_ARIA_LABEL,
} from './constants.js';
import { SparklineRenderer } from './SparklineRenderer.js';
import { SparklineContainerHeightFontSize } from './types/sparkline-container-height.js';
import type { SparklineDisplayProps } from './types/sparkline-display-props.js';
import { calculateArcRadius } from './utils/arc-radius.js';
import { getContextValueYPositions } from './utils/get-adjusted-context-value-y-position.js';
import { getContextValueLayout } from './utils/get-context-value-layout.js';
import { getFormattedContextValues } from './utils/get-formatted-context-values.js';
import { getShowContextValues } from './utils/get-show-context-values.js';
import { getSparklineHeight } from './utils/get-sparkline-height.js';
import { getSparklineXScale } from './utils/get-sparkline-x-scale.js';
import { getSparklineYScale } from './utils/get-sparkline-y-scale.js';
import { getXAxisConfig } from './utils/get-x-axis-config.js';
import { mapContainerHeightToVariant } from './utils/map-container-height-to-variant.js';
import { sanitizeSparklineData } from './utils/sanitize-sparkline-data.js';
import { calculateStrokeWidth } from './utils/stroke-width.js';
import { mapToThresholdsIndicators } from './utils/threshold-indicators.js';
import { useCurrentTheme } from '../../../../core/hooks/useCurrentTheme.js';
import { useObserveContainerSize } from '../../hooks/useObserveContainerSize.js';
import { Timeseries } from '../../types/timeseries.js';
import { getFormattedTicks } from '../../utils/formatter-by-ticks.js';
import { getValidColor } from '../../utils/getValidColor.js';
import { getHorizontalNumTicks } from '../../utils/ticks.js';
import { TimeBottomAxis } from '../axes/components/TimeBottomAxis.js';
import { Canvas } from '../canvas/Canvas.js';
import { CanvasContext } from '../canvas/CanvasContext.js';
import type { CanvasShape } from '../canvas/types/canvas-shape.js';
import { createCanvasGradient } from '../canvas/utils/create-canvas-gradient.js';
import {
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
} from '../grid/GridLine.js';
import { ThresholdIndicator } from '../threshold-bar/indicators/ThresholdIndicator.js';

interface SparklineContainerProps extends SparklineDisplayProps {
  data: Timeseries;
}

/** Clamps a context value Y position within the sparkline height bounds. */
function getYPosition(height: number, adjustedY: number): number {
  return Math.max(0, Math.min(height, adjustedY));
}

/**
 * SparklineContainer component
 */
export const SparklineContainer = (props: SparklineContainerProps) => {
  const {
    data,
    color,
    variant = 'line',
    showTicks = false,
    thresholds = [],
    gapPolicy = 'connect',
    showContextValues = DEFAULT_SHOW_CONTEXT_VALUES,
    curve,
    ticksColor,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    className,
    style: consumerStyle,
    labelsAlignment = 'left',
    labelsFixedWidth,
  } = props;

  const [containerDimension, sizeObserverRef] = useObserveContainerSize();

  const theme = useCurrentTheme();

  const shouldShowTicks = showTicks && containerDimension.height >= 40;

  const { width: containerWidth } = containerDimension;

  const xAxis = getXAxisConfig(props.xAxis);
  const yAxis = {
    ...defaultYAxisConfig,
    ...props.yAxis,
  };

  const { sanitizedSeries, domainValues } = sanitizeSparklineData(
    data,
    gapPolicy,
    xAxis,
  );

  const { showContextMin, showContextMax } = showContextValues
    ? getShowContextValues(yAxis, domainValues)
    : { showContextMin: false, showContextMax: false };

  const height = getSparklineHeight(containerDimension.height, shouldShowTicks);

  const isBarVariant = variant === 'bar';

  const yScale = getSparklineYScale(domainValues, height, yAxis, isBarVariant);

  const { formattedContextMaxValue, formattedContextMinValue } =
    getFormattedContextValues({
      unit: data?.unit,
      formatter: props.yAxis?.formatter,
      domainValues,
    });

  const { chartLeftOffset, chartWidth } = getContextValueLayout({
    containerHeight: containerDimension.height,
    containerWidth,
    showContextValues,
    formattedContextMaxValue,
    formattedContextMinValue,
    labelsAlignment,
    labelsFixedWidth,
  });

  const strokeWidth = calculateStrokeWidth(height, chartWidth);
  const arcRadius = calculateArcRadius(height, chartWidth);

  const xScale = getSparklineXScale(
    sanitizedSeries,
    chartWidth,
    strokeWidth / 2,
    isBarVariant,
  );

  const horizontalNumTicks = getHorizontalNumTicks(chartWidth);
  const { ticks, formattedTicks } = getFormattedTicks(
    horizontalNumTicks,
    xScale,
    undefined,
    undefined,
  );

  const thresholdIndicators = mapToThresholdsIndicators(
    thresholds,
    chartWidth,
    height,
    xScale,
    yScale,
  );

  const validatedColor = getValidColor(color, DEFAULT_SCALE_FALLBACK_COLOR);

  const [min, max] = yScale.domain();
  const displayZeroLine = min <= 0 && max >= 0;
  const zeroLineYPos = yScale(0) ?? -1;

  // Dashed lines sit at the raw data y-positions; text labels are pushed apart
  // if their text boxes would overlap, without moving the dashed lines.
  const rawMinY = yScale(domainValues.min);
  const rawMaxY = yScale(domainValues.max);

  const containerHeight = mapContainerHeightToVariant(
    containerDimension.height,
  );

  const fontSize =
    containerDimension.height >= FONT_SIZE_BREAKING_POINT
      ? SparklineContainerHeightFontSize.GREATER_OR_EQUAL_32
      : SparklineContainerHeightFontSize.SMALLER_THAN_32;

  const minLabelsDistance =
    fontSize === SparklineContainerHeightFontSize.GREATER_OR_EQUAL_32
      ? CONTEXT_VALUE_MIN_LABELS_DISTANCE_LARGE
      : CONTEXT_VALUE_MIN_LABELS_DISTANCE_SMALL;

  const maxTextOffset = CONTEXT_VALUE_TEXT_OFFSET_MAX;
  const minTextOffset =
    fontSize === SparklineContainerHeightFontSize.SMALLER_THAN_32
      ? CONTEXT_VALUE_TEXT_OFFSET_MIN_SMALL
      : CONTEXT_VALUE_TEXT_OFFSET_MIN_LARGE;

  const { adjustedMinLabelY, adjustedMaxLabelY } = getContextValueYPositions({
    rawMinY,
    rawMaxY,
    showContextMin,
    showContextMax,
    minLabelsDistance,
    minTextOffset,
    maxTextOffset,
    chartHeight: height,
  });

  return (
    <div
      style={{ height: '100%' }}
      className={className}
      ref={sizeObserverRef}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-label={SPARKLINE_ARIA_LABEL}
      role="img"
    >
      <div
        style={{
          transform: `translate(${chartLeftOffset}px)`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <svg
          width={chartWidth}
          height={height}
          style={{ position: 'absolute' }}
        >
          {displayZeroLine && (
            <ZeroLine
              from={{ x: 0, y: zeroLineYPos }}
              to={{ x: chartWidth, y: zeroLineYPos }}
              stroke={DEFAULT_STROKE_COLOR}
              strokeWidth={DEFAULT_STROKE_WIDTH}
              data-testid="sparkline-zero-baseline"
            />
          )}
          {thresholdIndicators.length > 0 &&
            thresholdIndicators.map(
              ({ id, indicatorData, color, strokeOnly }) => (
                <ThresholdIndicator
                  key={`threshold-indicator-${id}`}
                  data={indicatorData}
                  color={color}
                  strokeOnly={strokeOnly}
                  isActive={false}
                />
              ),
            )}
        </svg>
        <div style={{ position: 'absolute', ...consumerStyle }}>
          <CanvasContext.Provider
            value={{
              shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
            }}
          >
            <Canvas width={containerWidth} height={height}>
              <SparklineRenderer
                data={sanitizedSeries.datapoints}
                width={chartWidth}
                height={height}
                xScale={xScale}
                yScale={yScale}
                strokeColor={validatedColor}
                fillColor={createCanvasGradient({
                  height,
                  color: validatedColor,
                  domain: yScale.domain(),
                  theme,
                })}
                variant={variant}
                strokeWidth={strokeWidth}
                radius={arcRadius}
                curve={curve}
                gapPolicy={gapPolicy}
              />
            </Canvas>
          </CanvasContext.Provider>

          {shouldShowTicks && (
            <TimeBottomAxis
              ticks={ticks}
              tickColor={ticksColor}
              formattedTicks={formattedTicks}
              scale={xScale}
              containerHeight={AXIS_HEIGHT}
              containerWidth={chartWidth - 16}
            />
          )}
        </div>
      </div>

      {showContextMin && (
        <ContextValueIndicator
          formattedValue={formattedContextMinValue}
          dottedLineWidth={chartWidth}
          labelsFixedWidth={labelsFixedWidth}
          yPosition={getYPosition(height, rawMinY)}
          labelYPosition={adjustedMinLabelY}
          containerHeight={containerHeight}
          fontSize={fontSize}
          labelsAlignment={labelsAlignment}
          chartLeftOffset={chartLeftOffset}
          color={ticksColor}
          sparklineHeight={height}
        />
      )}
      {showContextMax && (
        <ContextValueIndicator
          formattedValue={formattedContextMaxValue}
          dottedLineWidth={chartWidth}
          yPosition={getYPosition(height, rawMaxY)}
          labelYPosition={adjustedMaxLabelY}
          containerHeight={containerHeight}
          fontSize={fontSize}
          labelsAlignment={labelsAlignment}
          chartLeftOffset={chartLeftOffset}
          labelsFixedWidth={labelsFixedWidth}
          color={ticksColor}
          sparklineHeight={height}
          isMax
        />
      )}
    </div>
  );
};

SparklineContainer['displayName'] = 'SparklineContainer';
